import { computed, DOCUMENT, Injectable, inject, type OnDestroy, signal } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    finalize,
    type Observable,
    type Subscription,
    tap,
    timer,
} from 'rxjs';

import { JOB_BOARD_PATH } from '@app/shared/constants/api-route.config';
import { SCHEDULED_JOB_STATUS } from '@app/shared/constants/shared.constant';
import type { JobBoardAdapter } from '@app/shared/models/job-board.model';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { RestService } from '@app/shared/services/rest.service';

@Injectable({
    providedIn: 'root',
})
export class JobBoardService implements OnDestroy {
    protected readonly endpoint = JOB_BOARD_PATH;

    protected readonly api = inject(RestService);
    private readonly document = inject(DOCUMENT);

    private jobSubscriber: Subscription;

    private pollNotifications: () => Subscription;

    private readonly jobBoardRefreshingSubject$ = new BehaviorSubject<boolean>(null);

    /**
     * Usecase: Trigger it to perform side-effects while jobBoardData's API is called
     * Eg. Used to reload dx-data-grid's DataSource(s) in JobHistory, JobBoard, etc.
     */
    readonly jobBoardRefreshing$ = this.jobBoardRefreshingSubject$.asObservable();

    readonly lastUpdatedTime = signal<Date>(null);

    readonly isLoading = signal(false);

    private readonly appConfigService = inject(AppConfigService);

    /**
     * Interval is in units of millisecond
     */
    readonly jobPullInterval = computed(
        () => this.appConfigService.appConfig().jobPullInterval || 60000,
    );

    readonly cards = signal([
        {
            title: 'Running',
            tooltipData: 'Total number of jobs that are running in the system right now',
            count: 0,
            name: SCHEDULED_JOB_STATUS.RUNNING,
            status: SCHEDULED_JOB_STATUS.RUNNING,
            statusClass: 'tw:text-amber-600',
            icon: 'bootstrapRocketTakeoffFill',
        },
        {
            title: 'Queued',
            tooltipData: 'Total number of jobs that are queued',
            count: 0,
            name: SCHEDULED_JOB_STATUS.QUEUED,
            status: SCHEDULED_JOB_STATUS.QUEUED,
            statusClass: 'tw:text-purple-600',
            icon: 'bootstrapHourglassSplit',
        },
        {
            title: 'Compiling',
            tooltipData: 'Total number of jobs that are compiling',
            count: 0,
            name: SCHEDULED_JOB_STATUS.COMPILING,
            status: SCHEDULED_JOB_STATUS.COMPILING,
            statusClass: 'tw:text-slate-500',
            icon: 'bootstrapGearFill',
        },
        {
            title: 'Total',
            tooltipData: 'Total number of jobs that are compiling, queued or running',
            count: 0,
            name: 'TOTAL',
            status: SCHEDULED_JOB_STATUS.COMPLETED, //Status is irrelevant for total, but set to completed to show green status in UI
            statusClass: 'tw:text-yellow-700',
            icon: 'bootstrapClipboardFill',
        },
    ]);

    constructor() {
        this.initializePolling();
    }

    /**
     * Gets job board data with the specified load options
     * @param params - The load options for the query
     * @returns An observable of the job board data response
     */
    getJobBoardData(params?: {
        skip?: number;
        take?: number;
        usernameCurrent?: boolean;
    }): Observable<{ result: JobBoardAdapter[]; pageInfo: { total: number } }> {
        return this.api.getWithParams(`${this.endpoint}`, params);
    }

    /**
     * Toggles the job polling on/off
     * @param enablePolling - Whether the polling should be enabled or not
     */
    toggleJobPolling(enablePolling: boolean): void {
        if (enablePolling && this.jobSubscriber) return;

        this.jobSubscriber?.unsubscribe();
        if (enablePolling) {
            this.jobSubscriber = this.pollNotifications();
        }
    }

    private initializePolling(): void {
        this.setTimerForJobPolling(this.jobPullInterval());
    }

    private setTimerForJobPolling(jobPullInterval: number) {
        this.pollNotifications = () => {
            return timer(jobPullInterval)
                .pipe(tap(() => this.fetchJobBoardData()))
                .subscribe();
        };
        this.jobSubscriber = this.pollNotifications();

        // Stop polling for inactive tabs
        this.document.addEventListener('visibilitychange', () => {
            this.toggleJobPolling(!this.document.hidden);
        });
    }

    /**
     * Fetches the current job board data and updates the cards
     */
    fetchJobBoardData(): void {
        this.isLoading.set(true);
        this.jobBoardRefreshingSubject$.next(true);

        this.api
            .get<{ result: Record<SCHEDULED_JOB_STATUS | 'total', number> }>(
                `${this.endpoint}stats`,
            )
            .pipe(
                catchError(error => {
                    console.error('Error fetching job board data:', error);
                    throw error;
                }),
                finalize(() => this.isLoading.set(false)),
            )
            .subscribe(response => {
                this.lastUpdatedTime.set(new Date());
                this.updateCards(response.result);
            });
    }

    private updateCards(data: Record<SCHEDULED_JOB_STATUS | 'total', number>) {
        this.cards.update(cards => {
            cards.forEach(card => {
                card.count = data[card.name as SCHEDULED_JOB_STATUS] || 0;
            });
            cards.find(x => x.name === 'TOTAL').count = data.total;
            return cards;
        });
    }

    ngOnDestroy() {
        this.jobSubscriber?.unsubscribe();
    }
}
